/** @flow */
import type { State } from "@store/reducers";
import type {
  EmployeeAbsence,
  AbsencesState,
  EmployeeAbsenceAlert,
  AddEmployeeAbsenceModal,
  TransferAbsenceModalType,
  EmployeeAbsenceShiftPatternOptions,
  EmployeeAbsenceInteraction,
  EditInteractionModal,
  EmployeeAbsenceAdditionalInfo,
  EditAdditionalInfoModal,
  AbsenceStatistics,
  AbsenceDocument,
  DeleteAbsenceDocumentModal,
  RtwInterviewForm,
  EmployeeAbsenceManagerNotes,
  EmployeeAbsenceNotification,
  EmployeeAbsenceNotificationModal,
  EmployeeNonCancelledAbsencesContent,
  TransferAbsenceConfirmationModalType,
} from "@modules/shared/reducer/employee-absences/types";
import { getPagination } from "@selectors";
import type { PaginationExtendedType, PropertiesSchema, ModalType } from "@types";

export const isLoading = (state: State): boolean =>
  !!state.shared.employeeAbsences.requestsInProgress;
export const isAbsenceDetailsLoading = (state: State): boolean =>
  !!state?.shared?.employeeAbsences?.absenceDetails?.requestsInProgress;

export const getEmployeeAbsences = (state: State): AbsencesState => state.shared.employeeAbsences;

export const getAbsences = (state: State): Array<EmployeeAbsence> =>
  getEmployeeAbsences(state).list.content;

export const getAbsenceDetails = (state: State): EmployeeAbsence | null => {
  const absenceDetails = state?.shared?.employeeAbsences?.absenceDetails;
  if (absenceDetails?.requestsInProgress) return null;
  else if (absenceDetails?.id) return absenceDetails;
  return null;
};
export const getAlertsTriggered = (state: State): Array<EmployeeAbsenceAlert> =>
  getEmployeeAbsences(state).alerts.content;

export const getAlertsTriggeredPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).alerts);

export const getAlertsTriggeredPage = (state: State): number | null =>
  getAlertsTriggeredPagination(state).pageNumber;

export const getAddAbsenceModal = (state: State): AddEmployeeAbsenceModal =>
  getEmployeeAbsences(state).modals.addAbsence;

export const getTransferAbsenceModal = (state: State): TransferAbsenceModalType =>
  getEmployeeAbsences(state).modals.transferAbsence;

export const getTransferAbsenceConfirmationModal = (
  state: State,
): TransferAbsenceConfirmationModalType =>
  getEmployeeAbsences(state).modals.transferAbsenceConfirmation;

export const getAbsencesPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).list);

export const getAbsencesSchema = (state: State): ?PropertiesSchema =>
  getEmployeeAbsences(state).absencesSchema;

export const getAbsenceShiftPattern = (state: State): Array<any> =>
  getEmployeeAbsences(state).absenceShiftPattern;

export const getAbsenceShiftPatternOptions = (state: State): ?EmployeeAbsenceShiftPatternOptions =>
  getEmployeeAbsences(state).absenceShiftPatternOptions;

export const getAbsenceShiftPatternDays = (state: State): ?Array<Object> =>
  getEmployeeAbsences(state)?.absenceShiftPatternOptions?.employeePosition[0]?.shiftPattern
    ?.shiftPatternDays;

export const getAbsenceShiftPatternModalOpen = (state: State): boolean =>
  getEmployeeAbsences(state).modals.shiftPattern.visible;

export const getInteractions = (state: State): Array<EmployeeAbsenceInteraction> =>
  getEmployeeAbsences(state).interactions.content;

export const getInteractionsPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).interactions);

export const getInteractionsPage = (state: State): number | null =>
  getInteractionsPagination(state).pageNumber;

export const getEditInteractionModal = (state: State): EditInteractionModal =>
  getEmployeeAbsences(state).modals.interaction;

export const getAdditionalInfo = (state: State): Array<EmployeeAbsenceAdditionalInfo> =>
  getEmployeeAbsences(state).additionalInfo.content;

export const getAdditionalInfoPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).additionalInfo);

export const getAdditionalInfoPage = (state: State): number | null =>
  getAdditionalInfoPagination(state).pageNumber;

export const getEditAdditionalInfoModal = (state: State): EditAdditionalInfoModal =>
  getEmployeeAbsences(state).modals.additionalInfo;

export const getAbsenceStatistics = (state: State): ?AbsenceStatistics =>
  getEmployeeAbsences(state).absenceStatistics;

export const getAbsenceStatisticsFilters = (state: State): Object =>
  ((state.form || {})["absence-statistics-filters"] || {}).values;

export const getAbsenceDocuments = (state: State): Array<AbsenceDocument> =>
  getEmployeeAbsences(state).absenceDocuments.content;

export const getRevisionHistory = (state: State) => getEmployeeAbsences(state).revisionHistory;

export const getEmployeeSMSMessages = (state: State) =>
  getEmployeeAbsences(state).employeeSMSMessages;

export const employeeNonCancelledAbsences = (state: State): EmployeeNonCancelledAbsencesContent => {
  return state.shared.employeeAbsences.employeeNonCancelledAbsences;
};

export const getAbsenceDocumentsPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).absenceDocuments);

export const getAbsenceDocumentsPage = (state: State): number | null =>
  getAbsenceDocumentsPagination(state).pageNumber;

export const getAddAbsenceDocumentModal = (state: State): ModalType =>
  getEmployeeAbsences(state).modals.addAbsenceDocument;

export const getDeleteAbsenceDocumentModal = (state: State): DeleteAbsenceDocumentModal =>
  getEmployeeAbsences(state).modals.deleteAbsenceDocument;

export const getAbsencesFilters = (state: State): Object => {
  let values = ((state.form || {})["absences-filters"] || {}).values;
  return { ...values, isCancelled: !!values?.isCancelled };
};

export const sagaGetAbsencesFilters = (state: State): Object => {
  // The BE logic works in a way that if provide isCancelled flag the results will filter by it
  // which will returns only cancelled or open/closed only absences.
  // the business requires when user select to show cancelled absence, that means to include them into the result set
  // that is why removing the isCancelled flag, the results set will includes open,closed and cancelled absences too
  let filters = { ...getAbsencesFilters(state) };
  if (filters.isCancelled === true) {
    delete filters.isCancelled;
  }

  return filters;
};

export const getRtwInterviewForm = (state: State): ?RtwInterviewForm => {
  const absenceDetails = getAbsenceDetails(state);
  if (!absenceDetails || !absenceDetails.rtwInterviewForms) return null;
  const [firstRtwFprm] = absenceDetails.rtwInterviewForms;
  return firstRtwFprm;
};

export const getManagerNotes = (state: State): Array<EmployeeAbsenceManagerNotes> =>
  getEmployeeAbsences(state).managerNotes.content;

export const getManagerNotesPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).managerNotes);

export const deleteNoteModalVisible = (state: State) =>
  getEmployeeAbsences(state).modals.deleteManagerNote.visible;

export const deleteNoteModalSelectedNote = (state: State) =>
  getEmployeeAbsences(state).modals.deleteManagerNote.note;

export const getEmployeeAbsenceNotifications = (state: State): Array<EmployeeAbsenceNotification> =>
  getEmployeeAbsences(state).absenceNotifications.content;

export const getEmployeeAbsenceNotificationsPagination = (state: State): PaginationExtendedType =>
  getPagination(getEmployeeAbsences(state).absenceNotifications);

export const getEmployeeAbsenceNotificationsPage = (state: State): number | null =>
  getEmployeeAbsenceNotificationsPagination(state).pageNumber;

export const getEmployeeAbsenceNotificationModal = (
  state: State,
): EmployeeAbsenceNotificationModal => getEmployeeAbsences(state).modals.absenceNotification;

export const getAddEditManagerNotesModal = (state: State) =>
  getEmployeeAbsences(state).modals.managerNotes;

export const getManagerNotesSchema = (state: State): ?PropertiesSchema =>
  getEmployeeAbsences(state).managerNotesSchema;

export const openRtwInterviewModal = (state: State) =>
  getEmployeeAbsences(state).modals.rtwInterview.visible;

export const selectBankHolidays = (state: State): Array<string> => {
  return getEmployeeAbsences(state).bankHolidays.map(i => i.date);
};
export const isCaptureBankHolidaysEnabled = (state: State): boolean | null => {
  return getEmployeeAbsences(state).captureAbsenceBankHolidays;
};

export const selectAbsenceCancelReasons = (state: State) => {
  return getEmployeeAbsences(state).absenceCancelReasons.map(i => ({
    const: i.id,
    title: i.name,
  }));
};
